import { css } from '@emotion/react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TaskAccordion from './TaskAccordion';
import ExpandableDetails from '../ExpandableDetails/ExpandableDetails';
import JsonViewer from '../JsonViewer/JsonViewer';
import DetailMeta, { TimestampValue } from '../DetailMeta';
import RunStateLabel from 'pages/WorkflowRunsPage/Runs/RunStateLabel';

import { useState } from 'react';

import killTask from 'store/killTask';
import KillConfirmDialog from '../KillConfirmDialog';
import pauseTask from 'store/pauseTask';
import resumeTask from 'store/resumeTask';
import useAutofetchedTaskDetailsAsync from 'store/useAutofetchedTaskDetailsAsync';

import ITaskDetails from 'types/ITaskDetails';

export default function TaskDetails({
  runId,
  taskId,
  wfPaused = false,
}: {
  runId: string;
  taskId: string;
  wfPaused?: boolean;
}) {
  const taskDetailsAsync = useAutofetchedTaskDetailsAsync(runId, taskId);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [pauseError, setPauseError] = useState<string | null>(null);
  const [confirmKill, setConfirmKill] = useState(false);

  switch (taskDetailsAsync.status) {
    case 'INIT':
    case 'PENDING':
      return (
        <TaskAccordion title={taskId} copyId={taskId}>
          <CircularProgress />
        </TaskAccordion>
      );
    case 'ERROR':
      return (
        <TaskAccordion title={taskId} copyId={taskId}>
          <p>Oops!</p>
          <p>Could not fetch task detail.</p>
        </TaskAccordion>
      );
    case 'SUCCESS':
    case 'REFRESH': {
      const taskDetails = taskDetailsAsync.data as ITaskDetails;

      const lastRun = taskDetails.runs && taskDetails.runs.slice(-1)[0];
      const traceback_raw = lastRun && lastRun.traceback_raw;

      const pausable = taskDetails.definition.supports_pause === true && taskDetails.state === 'RUNNING';
      const resumable = taskDetails.state === 'PAUSED';
      const killable = taskDetails.state === 'RUNNING' && !taskDetails.kill_requested;

      return (
        <TaskAccordion title={`${taskDetails.id} ${taskDetails.name}`} copyId={taskDetails.id}>
          <DetailMeta
            rows={[
              { label: 'State', value: <RunStateLabel state={taskDetails.state} /> },
              { label: 'Created', value: <TimestampValue iso={taskDetails.created_utc} /> },
              { label: 'Updated', value: <TimestampValue iso={taskDetails.updated_utc} /> },
            ]}
          />
          {taskDetails.runs && taskDetails.runs.length > 0 && (
            <div
              css={css`
                margin: 0 12px 16px;
                font-size: 14px;
              `}
            >
              <div
                css={css`
                  color: #666;
                  font-size: 12px;
                  letter-spacing: 0.04em;
                  text-transform: uppercase;
                  margin-bottom: 6px;
                `}
              >
                Runs
              </div>
              <ol
                css={css`
                  margin: 0;
                  padding: 0;
                  list-style: none;
                `}
              >
                {taskDetails.runs.map((run, index) => (
                  <li
                    key={index}
                    css={css`
                      display: grid;
                      grid-template-columns: 1.5rem minmax(7rem, auto) 1fr;
                      gap: 8px 12px;
                      align-items: baseline;
                      padding: 2px 0;
                    `}
                  >
                    <span
                      css={css`
                        color: #888;
                        font-variant-numeric: tabular-nums;
                      `}
                    >
                      {index + 1}
                    </span>
                    <RunStateLabel state={run.state} />
                    <TimestampValue iso={run.ended_utc} />
                  </li>
                ))}
              </ol>
            </div>
          )}
          {(pausable || resumable) && (
            <p>
              <Button
                variant="outlined"
                size="small"
                disabled={pauseBusy}
                onClick={() => {
                  setPauseBusy(true);
                  setPauseError(null);
                  (pausable ? pauseTask(runId, taskId) : resumeTask(runId, taskId))
                    .catch(() => setPauseError(pausable ? 'Pause failed' : 'Resume failed'))
                    .finally(() => setPauseBusy(false));
                }}
              >
                {pauseBusy && <CircularProgress size={16} sx={{ mr: 1 }} />}
                {pausable ? 'Pause task' : 'Resume task'}
              </Button>
              {pauseError && (
                <span
                  css={css`
                    color: #db0051;
                    margin-left: 8px;
                  `}
                >
                  {pauseError}
                </span>
              )}
              {resumable && wfPaused && (
                <span
                  css={css`
                    color: #b56552;
                    margin-left: 8px;
                  `}
                >
                  The workflow is paused — the task will re-queue but stays held until the workflow resumes.
                </span>
              )}
            </p>
          )}
          {killable && (
            <p>
              <Button
                variant="outlined"
                color="error"
                size="small"
                title="Terminate this task on its worker; it ends FAILED and can be reset"
                onClick={() => setConfirmKill(true)}
              >
                Kill task
              </Button>
              {confirmKill && (
                <KillConfirmDialog
                  taskIds={[taskId]}
                  onClose={() => setConfirmKill(false)}
                  onConfirm={() => killTask(runId, taskId)}
                />
              )}
            </p>
          )}
          {taskDetails.kill_requested && taskDetails.state === 'RUNNING' && (
            <p
              css={css`
                color: #db0051;
              `}
            >
              Kill requested — waiting for the worker to terminate the task; it will end FAILED.
            </p>
          )}
          {(taskDetails.pause_count ?? 0) > 0 && (
            <p
              css={css`
                color: #b56552;
              `}
            >
              Paused {taskDetails.pause_count} time{taskDetails.pause_count === 1 ? '' : 's'}
              {taskDetails.local_node_id ? ` — pinned to node ${taskDetails.local_node_id}` : ''}
              {taskDetails.retained_run_dir ? (
                <>
                  <br />
                  Checkpoint (retained run dir): <code>{taskDetails.retained_run_dir}</code>
                </>
              ) : null}
            </p>
          )}
          {(taskDetails.eviction_count ?? 0) > 0 && (
            <p
              css={css`
                color: #e07a26;
              `}
            >
              Evicted {taskDetails.eviction_count} time{taskDetails.eviction_count === 1 ? '' : 's'} — the task was
              stopped on its worker and re-queued.
            </p>
          )}
          {traceback_raw && (
            <ExpandableDetails
              summary="Error"
              contentPlaceholder="Traceback (only rendered when open)"
              copyText={traceback_raw}
              renderContent={() => (
                <Box
                  component="pre"
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    p: 1.5,
                    bgcolor: 'action.hover',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                  }}
                >
                  {traceback_raw}
                </Box>
              )}
            />
          )}
          <ExpandableDetails
            summary="Inputs"
            contentPlaceholder="Inputs as json (only rendered when open)"
            copyJson={taskDetails.inputs}
            renderContent={() => <JsonViewer json={taskDetails.inputs} />}
          />
          <ExpandableDetails
            summary="Outputs"
            contentPlaceholder="Outputs as json (only rendered when open)"
            copyJson={taskDetails.outputs}
            renderContent={() => <JsonViewer json={taskDetails.outputs} />}
          />
          <ExpandableDetails
            summary="Files"
            contentPlaceholder="Files as json (only rendered when open)"
            copyJson={taskDetails.files}
            renderContent={() => <JsonViewer json={taskDetails.files} />}
          />
          <ExpandableDetails
            summary="Local files"
            contentPlaceholder="Local files as json (only rendered when open)"
            copyJson={taskDetails.local_files ?? {}}
            renderContent={() => <JsonViewer json={taskDetails.local_files ?? {}} />}
          />
          <ExpandableDetails
            summary="Definition"
            contentPlaceholder="Definition as json (only rendered when open)"
            copyJson={taskDetails.definition}
            renderContent={() => <JsonViewer json={taskDetails.definition} />}
          />
        </TaskAccordion>
      );
    }
  }
}
